// Shared base interface
export interface BaseSop {
  id: number;
  serviceNo: string;
  name: string;
  version: string;
  sopTypeDisplay: string;
  sopSubtypeDisplay: string;
  sopType: string;
  sopSubtype: string;
  description: string;
  sopAssociationType: string | number;
  sopAssociationTypeDisplay: string;
  status: string | number;
  statusDisplay: string;
  createdDate: string;
  createdUserRemoteName: string;
  isActive: boolean;
  priority: string | number;
  priorityDisplay: string;
  rulesDuration: number;
  usageCount: number;
  taskCount: number;
  materialCostCount: number;
  rate: number;
  tax: string;
}
export interface UtilityService {
  id: number | null;
  name: string;
  serviceId: number;
  isActive: boolean;
  isMeterRequired: boolean;
  utilityUnit: string | null;
}
export interface UtilityServicesResponse {
  result: UtilityService[];
}
export interface ServiceAssociationChoice {
  key: number;
  value: string;
}
export interface ServiceAssociationResponse {
  result: ServiceAssociationChoice[];
}
// Used in summary view
export interface ServiceProcedureSummaryResponse {
  result: {
    totalTemplateCount: number;
    typeCount: number;
    subtypeCount: number;
    activeTemplateCount: number;
    avgRulesDuration: number | null;
  };
}

// Used in paginated response list
export interface SOPTemplateAPI extends BaseSop {
  task: Array<{
    name: string;
    time: string;
    downtime: string;
    skillId: number[];
    isPhotoReq: boolean | string;
    // isTaskMandatory: boolean | null;
  }>;
  material: Array<{
    cost: string;
    name: string;
    size: string;
    quantity: string;
  }>;
  totalDowntime: number | null;
  shortName: string;
  utilityServices: string[];
  estimatedOtherCost: number | null;
  taxName: string | null;
  estTotalCost: number;
}

export interface SOPTemplateResponse {
  pageNumber: number;
  count: number;
  next: string | null;
  previous: string | null;
  results: SOPTemplateAPI[];
}

// Normalized structure used by client
export interface Template {
  id: string;
  name: string;
  sopType: string;
  sopSubtype: string;
  association: string;
  type: string;
  subtype: string;
  isActive: boolean;
  description: string;
  status: string;
  version: string;
  createdAt: string;
  createdBy: string;
  taskCount: number;
  usageCount: number;
  totalCost: string;
  estTotalCost: string;
  sla: {
    value: string;
    progress: number;
  };
  serviceNo?: string;
}

// Task detail used in detail view
export interface SopTask {
  name: string;
  time: string;
  downtime: string;
  skillId: number[];
  isPhotoReq: string;
}

// Reading detail used in detail view
export interface SopReading {
  key: string;
  name: string;
  unit: string;
  default: boolean;
  maximum: string;
  minimum: string;
  isPhotoReq: string;
}

// Document metadata

// SOP details (extended from BaseSop)
export interface SopDetailsResponse {
  result: BaseSop & {
    remoteUtilityId: number;
    operationType: number;
    operationTypeDisplay: string;
    task: SopTask[];
    material: any; // replace with defined type as needed
    additionalData: any;
    readings: SopReading[];
    taskTimeCount: number | null;
    skills: any[];
    rules: any[];
    documents: SopDocument[];
    staffRequired: number;
    ruleNames: string[];
    totalDowntime: number | null;
    shortName: string;
    utilityServices: string[];
    estimatedOtherCost: number | null;
    materialCount: number;
    readingsCount: number;
    sopTypeDisplay: string;
    sopSubtypeDisplay: string;
    estTotalCost: number;
    taxDisplay: number;
  };
}

// Params for detail API
export interface SopDetailsParams {
  id: string;
  remoteUtilityId: string;
}
export interface SopTabDetailsParams {
  id: string;
  remoteUtilityId: string;
  fieldName: string;
}
export interface SopTabDetailsResponse {
  result: any[];
}
///stepper types
// SOP Builder Type Definitions

export interface SopFormData {
  initialSetup: InitialSetupData;
  basicInfo: BasicInfoData;
  safety: SafetyData;
  tasks: TaskData[];
  materials: MaterialData[];
  equipment: EquipmentData[];
  readings: ReadingData[];
  helpContent: string;
  resources: ResourceData[];
  contacts: ContactData[];
  fieldNotes: string;
  suggestionPrompt: string;
  allowRating: boolean;
  trackCompletion: boolean;
  helpDocuments?: any[];
  unitReadings?: any[];
}

export interface InitialSetupData {
  name: string;
  description: string;
  utilityType: string;
  associatedWith: string;
  assetType?: string[];
  systemType?: string[];
  facilityType?: string[];
  networkType?: string[];
  actionType?: string;
  operationType?: string;
}

export interface BasicInfoData {
  sopName: string;
  shortName: string;
  description: string;
  utilityType: string; // Changed from UtilityType enum to string
  type: string;
  typeCode?: string; // Added to store API type code
  subType: string;
  subTypeCode?: string; // Added to store API subtype code
  priority: Priority;
  associatedWith: string; // Changed from ServiceAssociation enum to string
  sla: string;
  estimatedLabourCost: string;
  estimatedOtherCost: string; 
  taxRate: string;
  requiredFieldforceCount: string;
  assetType?: string[];
  systemType?: string[];
  facilityType?: string[];
  networkType?: string[];
  actionType?: string;
  operationType?: string;
  customType?: string;
  customSubType?: string;
  customAssetType?: string;
  customActionType?: string;
  customOperationType?: string;
  slaHours: any;
}

export interface SafetyData {
  ppe: string[];
  riskLevel: string;
  safetyNotes: string;
  safetyPackage: string;
  safetyProtocols: string[];
  fieldRealities: string;
  environmentalVariables: string[];
  selectedPackageId: number | null;
}

export interface TaskData {
  id: number;
  title: string;
  description?: string;
  duration: number;
  mandatory: boolean;
  downtime: number;
  skills: string[];
  photoRequired: boolean;
}

export interface MaterialData {
  name: string;
  quantity: string;
  size?: string;
  cost?: string;
}

export interface EquipmentData {
  name: string;
  condition: EquipmentCondition;
}

export interface ReadingData {
  name: string;
  unit: string;
  min: string;
  max: string;
  // criticality: ReadingCriticality;
  photoRequired?: boolean;
}

export interface ResourceData {
  id: number;
  name: string;
  type: ResourceType;
  content: string;
  url?: string;
}

export interface ContactData {
  id: number;
  name: string;
  role: string;
  phone: string;
  email: string;
}

// Enums and Union Types
export type UtilityType = "electric" | "water" | "gas" | "hvac" | "other";

export type Priority = "critical" | "high" | "medium" | "low";

export type ServiceAssociation =
  | "consumer"
  | "meter"
  | "asset"
  | "complaint"
  | "generic";

export type EquipmentCondition = "new" | "good" | "fair" | "poor";

export type ReadingCriticality = "low" | "medium" | "high";

export type ResourceType = "video" | "file" | "instruction";

export type SafetyPackageType =
  | "electrical"
  | "confined"
  | "hazardous"
  | "heights"
  | "standard";

// Step Component Props Interface
export interface StepComponentProps {
  state: SopFormData;
  onUpdateState: (updates: Partial<SopFormData>) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
}

// Material Package Interfaces
export interface MaterialPackage {
  name: string;
  description: string;
  materials: MaterialData[];
}

export interface MaterialPackages {
  [key: string]: MaterialPackage;
}

// Safety Package Interface
export interface SafetyPackage {
  id: SafetyPackageType;
  title: string;
  description: string;
  icon: any; // React Icon Component
  color: string;
  iconColor: string;
}

// Task Template Interface
export interface TaskTemplate {
  title: string;
  description: string;
  duration: number;
  mandatory: boolean;
  downtime: number;
  skills: string[];
  photoRequired: boolean;
  type: "basic" | "standard" | "complex";
  timeRange: string;
}

// Reading Set Interface
export interface ReadingSet {
  id: string;
  name: string;
  description: string;
  readings: ReadingData[];
}

// Utility Type Options Interface
export interface UtilityTypeOption {
  value: UtilityType;
  label: string;
  icon: any; // React Icon Component
  bgColor: string;
  textColor: string;
  borderColor: string;
  iconColor: string;
}

// SOP Types and Subtypes
export interface SopTypesAndSubtypes {
  [key: string]: string[];
}

// Master Data Interfaces
export interface MasterDataOption {
  value: string;
  label: string;
}

// Form Validation Interfaces
export interface ValidationErrors {
  [field: string]: string;
}

export interface FormValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

// Stepper Configuration Interface
export interface StepConfiguration {
  title: string;
  description: string;
  slug: string;
  component: React.ReactElement;
  optional: boolean;
  infoTitle: string;
  infoDescription: string;
}

// SOP Builder State Interface
export interface SopBuilderState {
  currentStep: number;
  formData: SopFormData;
  isLoading: boolean;
  error: string | null;
  validationErrors: ValidationErrors;
}

// API Response Interfaces (for when API integration is added)
export interface SopApiResponse {
  id: string;
  status: "draft" | "published" | "archived";
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  data: SopFormData;
}

export interface CreateSopRequest {
  data: SopFormData;
  status: "draft" | "published";
}

export interface UpdateSopRequest {
  id: string;
  data: Partial<SopFormData>;
  status?: "draft" | "published";
}

// Export utility functions type definitions
export type GetPackageKeyFunction = (
  type: string,
  subtype: string,
) => string | null;
export type GetAllRelevantPackagesFunction = (
  type: string,
  subtype: string,
  utilityType: string,
) => string[];

// Component State Interfaces
export interface SopBasicInfoStepState {
  showAssetFields: boolean;
  selectedType: string;
  availableSubtypes: string[];
  showCustomFields: {
    type: boolean;
    subType: boolean;
    assetType: boolean;
    actionType: boolean;
    operationType: boolean;
  };
}

export interface SopSafetyStepState {
  selectedPackage: SafetyPackageType | null;
}

export interface SopTasksStepState {
  viewMode: "simple" | "advanced";
  selectedTemplateType: "basic" | "standard" | "complex" | null;
}

export interface SopMaterialsStepState {
  packageKeys: string[];
}

export interface SopReadingsStepState {
  activeTab: "all" | "critical";
}

export interface SopHelpStepState {
  showAddResource: boolean;
  editingResourceId: number | null;
  newResourceType: ResourceType;
  newResourceName: string;
  newResourceContent: string;
}

export interface SopReviewStepState {
  expandedSections: string[];
  previewMode: boolean;
  selectedPublishOption: "draft" | "standard";
}

// Constants and Configuration Types
export interface AppConfiguration {
  enableApiSave: boolean;
  enableRouting: boolean;
  persistProgress: boolean;
  storageKey: string;
  autoSave: boolean;
}

// Mode-specific interfaces
export type SopBuilderMode = "create" | "edit" | "view";

export interface SopBuilderProps {
  mode: SopBuilderMode;
  templateId?: string;
  onSave?: (data: SopFormData) => void;
  onComplete?: (data: SopFormData) => void;
  onCancel?: () => void;
  readOnly?: boolean;
  initialData?: Partial<SopFormData>;
}

// Error handling interfaces
export interface SopBuilderError {
  code: string;
  message: string;
  field?: string;
  step?: number;
}

// Default values and constants
export const DEFAULT_SOP_FORM_DATA: SopFormData = {
  initialSetup: {
    name: "",
    description: "",
    utilityType: "",
    associatedWith: "",
    assetType: "",
    actionType: "",
    operationType: "",
  },
  basicInfo: {
    sopName: "",
    shortName: "",
    description: "",
    utilityType: "", // Changed from "water" to empty string
    type: "",
    typeCode: "", // Added
    subType: "",
    subTypeCode: "", // Added
    priority: "medium",
    associatedWith: "", // Changed from "consumer" to empty string
    sla: "",
    slaHours: "",
    estimatedLabourCost: "",
    taxRate: "",
    requiredFieldforceCount: "",
  },
  safety: {
    ppe: [],
    riskLevel: "",
    safetyNotes: "",
    safetyPackage: "",
    safetyProtocols: [],
    fieldRealities: "",
    environmentalVariables: [],
    selectedPackageId: null,
  },
  tasks: [],
  materials: [],
  equipment: [],
  readings: [],
  helpContent: "",
  resources: [],
  contacts: [],
  fieldNotes: "",
  suggestionPrompt: "",
  allowRating: false,
  trackCompletion: false,
};

// Export default task for new instances
export const DEFAULT_TASK: Omit<TaskData, "id"> = {
  title: "",
  description: "",
  duration: 0,
  mandatory: false,
  downtime: 0,
  skills: [],
  photoRequired: false,
};

// Export default reading for new instances
export const DEFAULT_READING: ReadingData = {
  name: "",
  unit: "",
  min: "",
  max: "",
  // criticality: "medium",
  photoRequired: false,
};

////Stepper types end
export interface SopConfigItem {
  name: string;
  isActive: boolean;
  createdBy: string;
  createdDate: string;
  code: string;
  hierarchyNames: {
    sopType: string;
    sopSubtype?: string;
  };
  hierarchyCodes?: {
    sopType: string;
    sopSubtype?: string;
  };
}

export interface SopConfigResponse {
  result: SopConfigItem[];
}
export interface SopTask {
  code: string;
  name: string;
  time: string;
  downtime: string;
  skillId: number[];
  isActive: boolean;
  createdBy?: string;
  createdDate: string;
  isPhotoReq: string;
  operationType: string;
  operationTypeDisplay: string;
}

export interface SopTasksResponse {
  result: SopTask[];
}
export interface SopMaterials {
  code: string;
  name: string;
  size: string;
  quantity: number;
  isActive: boolean;
  createdDate: string;
  costPerUnit: number;
  operationType: string;
  operationTypeDisplay: string;
  createdBy?: string;
}
export interface SopMaterialsResponse {
  result: SopMaterials[];
}
export interface SopReadings {
  key: string;
  code: string;
  name: string;
  unit: string;
  default: boolean;
  maximum: string;
  minimum: string;
  isActive: boolean;
  createdDate: string;
  isPhotoReq: string;
  operationType: string;
  operationTypeDisplay: string;
  createdBy?: string;
}

export interface SopReadingsResponse {
  result: SopReadings[];
}
export type sopFilters = {
  page: number;
  limit: number;
  remote_utility_id: string;
  status?: string;
  utilityType?: string;
  priority?: string;
  search_data?: string;
  is_draft?: boolean; // To differentiate between active and draft templates
};

// You might also want to add these utility types
export type FilterStatus = "all" | "active" | "draft" | "archived" | "inactive";
export type FilterUtilityType = "all" | "water" | "gas" | "electric";
export type FilterPriority = "all" | "critical" | "high" | "medium" | "low";
export interface CreateMasterSopRequest {
  name: string;
  shortName: string;
  description: string;
  utilityServices: string[];
  sopAssociationType: number;
  sopType: string;
  sopSubtype: string;
  rulesDuration: string;
  rate: string;
  estimatedOtherCost: string;
  tax: string;
  staffRequired: string;
  rules: number[];
  operationType: number;
  remoteUtilityId: number;
  additionalData?: any;
}
export interface CreateSopResponse {
  result: {
    id: number;
    serviceNo: string;
    remoteUtilityId: number;
    name: string;
    sopType: string;
    sopSubtype: string;
    status: number;
    statusDisplay: string;
    operationType: number;
    operationTypeDisplay: string;
    rate: number;
    skills: any[];
    tax: string;
    sopAssociationType: number;
    description: string;
    task: any;
    material: any;
    additionalData: any;
    readings: any[];
    taskTimeCount: any;
    materialCostCount: any;
    version: string;
    staffRequired: number;
    totalDowntime: any;
    shortName: string;
    utilityServices: string[];
    estimatedOtherCost: any;
    createdUserRemoteName: string;
    sopAssociationTypeDisplay: string;
    actionType: any;
    assetType: any;
    priority: number;
    priorityDisplay: string;
    rulesDuration: number;
  };
}
export interface UpdateSopTasksRequest {
  task: Array<{
    name: string;
    time: string;
    downtime: string;
    skillId: number[];
    // isTaskMandatory: boolean;
    isPhotoReq: string;
  }>;
  remoteUtilityId: number;
}
export interface UpdateSopMaterialsRequest {
  material: Array<{
    cost: string;
    name: string;
    size: string;
    quantity: string;
  }>;
  remoteUtilityId: number;
}
export interface UpdateSopReadingsRequest {
  readings: Array<{
    name: string;
    unit: string;
    maximum: string;
    minimum: string;
    isPhotoReq: string;
  }>;
  remoteUtilityId: number;
}
export interface UpdateSopResponse {
  result: {
    id: number;
    serviceNo: string;
    remoteUtilityId: number;
    name: string;
    sopType: string;
    sopSubtype: string;
    status: number;
    statusDisplay: string;
    operationType: number;
    operationTypeDisplay: string;
    rate: number;
    skills: number[];
    tax: string;
    sopAssociationType: number;
    description: string;
    task: any[];
    material: any[];
    additionalData: any;
    readings: any[];
    taskTimeCount: any;
    materialCostCount: number;
    version: string;
    staffRequired: number;
    totalDowntime: any;
    shortName: string;
    utilityServices: string[];
    estimatedOtherCost: any;
    createdUserRemoteName: string;
    sopAssociationTypeDisplay: string;
    actionType: any;
    assetType: any;
    priority: number;
    priorityDisplay: string;
    rulesDuration: number;
  };
}
export interface SopDocument {
  type: number; // 0=instruction, 1=video/link, 2=file
  description?: string; // For type 0 (instructions)
  link?: string; // For type 1 (video/external links)
  id?: number; // For existing documents
}

export interface UpdateSopDocumentsRequest {
  remoteUtilityId: string;
  documents: string; // JSON stringified array of SopDocument
  file?: File; // For file uploads (type 2)
}

export interface UpdateSopDocumentsResponse {
  result: {
    id: number;
    // Add other response fields as needed
  };
}
